import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { PaymentHistoryRow, PaymentHistoryStatus, SubscriptionPlanType } from 'types'
import { formatDate } from 'utils'
import { getPlanLabel } from '../constants'

const STATUS_CONFIG: Record<
  string,
  { label: string; color: 'success' | 'error' | 'warning' | 'default' | 'info' }
> = {
  paid: { label: 'Paid', color: 'success' },
  open: { label: 'Open', color: 'warning' },
  draft: { label: 'Draft', color: 'default' },
  void: { label: 'Void', color: 'default' },
  uncollectible: { label: 'Uncollectible', color: 'error' },
  PROMO_ACTIVE: { label: 'Promo Active', color: 'info' },
  PROMO_INACTIVE: { label: 'Promo Inactive', color: 'default' },
  NOT_AVAILABLE: { label: 'N/A', color: 'default' },
}

function StatusChip({ status }: { status: PaymentHistoryStatus | null }) {
  if (!status) return <Typography variant="body2">—</Typography>
  const config = STATUS_CONFIG[status] ?? { label: status, color: 'default' as const }
  return <Chip label={config.label} color={config.color} size="small" />
}

export function PaymentHistoryTable({ history }: { history: PaymentHistoryRow[] }) {
  const sortedHistory = [...history].sort((itemA, itemB) => {
    if (!itemA.paymentDate) return 1
    if (!itemB.paymentDate) return -1
    return new Date(itemB.paymentDate).getTime() - new Date(itemA.paymentDate).getTime()
  })

  if (history.length === 0) {
    return (
      <Box>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Payment History
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No payment history available.
        </Typography>
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Payment History
      </Typography>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Plan</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Payment Method</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedHistory.map((item, index) => {
              const planLabel = getPlanLabel(item.planType as SubscriptionPlanType)
              return (
                <TableRow key={index}>
                  <TableCell>
                    {item.paymentDate ? formatDate(item.paymentDate, 'MM.DD.YY') : '—'}
                  </TableCell>
                  <TableCell>{planLabel}</TableCell>
                  <TableCell>
                    <StatusChip status={item.status} />
                  </TableCell>
                  <TableCell>{item.paymentMethod ?? '—'}</TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
